
import type {
  Inventory,
  QuestConfirmPrompt_,
  QuestDialog_,
  QuestEntry_,
  QuestInputPrompt_,
  QuestItemPrompt_,
  QuestLine_,
} from "../net/connection";
import {
  QUEST_LINE_ITEM,
  QUEST_LINE_REWARD,
  QUEST_NO_DIALOG,
} from "../net/connection";
import { yieldToCandidateWindow } from "./ime";
import { applyItemIcon } from "./itemicon";
import { ItemTooltip, attachHoverTooltip, slotTooltipData, type ItemTooltipData } from "./tooltip";
import { BOARD_FILL, BOARD_SLICE, CHROME_ASSETS, elem, styleInstaller } from "./chrome";

export interface QuestActions {
  /** Answer the open page. `choice` is 1-based; 0 is "continue". */
  onReply?: (token: number, choice: number) => void;
  /** Open a quest from the log, by the server's own row index. Runs the row's
   *  `button` body -- `CQuestManager::QuestButton` (`questmanager.cpp:586`),
   *  which is where the shipped scripts put the interactive content. */
  onOpen?: (index: number) => void;
  onDescribe?: (index: number) => void;
  /** Fired when a conversation opens, so whatever else was on screen can move
   *  out of the way. A dealer's sell mode is the one that matters: clicking the
   *  General Store Saleswoman is both a quest and a shop. */
  onOpened?: () => void;
}

/** The legacy selector's `SetInventoryItem` reads the same inventory facts as
 * the bag tooltip. Keep the prompt's hover content on that shared contract so
 * a player can inspect the offered item before answering the quest. */
function tooltipDataFor(slot: Inventory["slots"][number]): ItemTooltipData {
  return slotTooltipData(slot, { price: slot.sellValue });
}

const STYLE_ID = "quest-style";
/** `QuestDialog.QUEST_BUTTON_MAX_NUM` (`uiquest.py:250`). */
export const QUEST_CHOICES_PER_PAGE = 8;

/** The board `uiscript/questdialog.py` centres in its 800x450 float window:
 *  a 350x300 `thinboard` (`questdialog.py:26-27`). */
export const QUEST_BOARD_WIDTH = 350;
export const QUEST_BOARD_HEIGHT = 300;

/** `OnUpdate` offsets the event set by 20 from the board's corner and gives it
 *  `board.GetWidth() - 40` (`uiquest.py:381-384`). */
export const QUEST_BOARD_INSET = 20;

/** `MakeEachButton`'s answer bar, `MakeNextButton`'s lone bar, and
 *  `MakeNextPrevPageButton`'s pager (`uiquest.py:498,444,507`). */
export const QUEST_ANSWER_BUTTON_WIDTH = 200;
export const QUEST_BUTTON_HEIGHT = 26;
export const QUEST_NEXT_BUTTON_WIDTH = 100;
export const QUEST_PAGE_BUTTON_WIDTH = 52;

/** `season1/icon/scroll_close.tga` is 32x32 (`localeinfo.py:626-629`). */
export const QUEST_LETTER_ICON = 32;

/** `QUEST_ICON_BACKGROUND` (`characterwindow.py:3`), the 32x32 plate under
 *  every `Quest_Slot` cell of the CharacterWindow quest page (`:739-751`). */
const QUEST_SLOT_ART = "/ui/textures/game/quest/slot_base.png";
/** `QUEST_SKIN_NORMAL`, the skin of a page that names none (`questmanager.cpp:86`). */
export const QUEST_SKIN_NORMAL = 1;

/** The whole look of both panels. See the module docstring for why it is here. */
const STYLE = `
.quest-log {
  position: fixed;
  left: max(16px, env(safe-area-inset-left));
  /* The top-left corner no longer holds a standing "vitals" card -- the 2014
     client has no such text readout, so that invented panel was retired in
     favour of the task bar's own HP/SP/EXP gauges (uitaskbar.py:525-535).
     What can still occupy this corner is the buffs row (#overlay's one other
     flow child, .affects, 34px chips plus the column's 8px gap), so this
     still clears it rather than assuming the corner is always empty. */
  top: max(56px, calc(env(safe-area-inset-top) + 56px));
  z-index: 35;
  width: min(260px, 46vw);
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.78);
  border: 1px solid rgba(255, 236, 170, 0.18);
  pointer-events: auto;
  font: 12px/1.45 system-ui, sans-serif;
  color: #d8dee6;
}
/* CharacterWindow QUEST page: fill the 253-wide tab, not a corner card
   (characterwindow.py:726-784), top edge first, so it opens on its first
   Quest_Slot (:739-751). */
.charwin-page .quest-log {
  position: absolute;
  inset: 0;
  z-index: auto;
  width: auto;
  padding: 8px 10px;
  border-radius: 0;
  background: transparent;
  border: 0;
  overflow: auto;
}
.charwin-page .quest-log-title { display: none; }
.quest-log-title {
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9b9484;
  margin-bottom: 6px;
}
.quest-row {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  border-left: 2px solid rgba(255, 210, 120, 0.5);
  padding: 3px 0 5px 8px;
  margin-bottom: 6px;
  color: inherit;
  font: inherit;
  cursor: pointer;
}
.quest-row:hover { border-left-color: #ffd27a; }
/* Quest_Slot (characterwindow.py:739-751): a one-column grid_table at (18,20)
   whose cells are 32x32 and drawn on QUEST_ICON_BACKGROUND, stepping 32 with
   28 blank between. The plate is the page's, not the corner card's, so only
   the CharacterWindow tab wears it. */
.charwin-page .quest-log .quest-row {
  box-sizing: border-box;
  min-height: 32px;
  padding: 0 0 0 40px;
  margin-bottom: 28px;
  border-left: 0;
  background: url("${QUEST_SLOT_ART}") 0 0 / 32px 32px no-repeat;
  image-rendering: pixelated;
}
.quest-row-entry { display: flex; align-items: flex-start; gap: 4px; }
.quest-row-entry .quest-row { flex: 1 1 auto; min-width: 0; }
.quest-row-info {
  flex: 0 0 auto;
  align-self: center;
  background: transparent;
  border: 1px solid rgba(255, 210, 120, 0.45);
  border-radius: 4px;
  color: #9b9484;
  font: inherit;
  line-height: 1;
  padding: 2px 6px;
  margin-bottom: 6px;
  cursor: pointer;
}
.quest-row-info:hover { color: #ffd27a; border-color: #ffd27a; }
.quest-row-title { color: #ffd27a; font-weight: 600; }
.quest-row-line { color: #a9b1bb; font-size: 0.92em; }
.quest-row-count { color: #e6e0cf; }

/* uiscript/questdialog.py: an 800x450 float window (:10-11) whose one child
   is a 350x300 thinboard (:17,26-27) centred on both axes (:23-24); the
   window itself is centred by LoadDialog's SetCenterPosition
   (uiquest.py:376). So the board sits in the middle of the screen, not at
   its foot. The frame is ui.ThinBoard -- the eight pattern/thinboard_*.tga
   pieces over BOARD_COLOR = rgba(0,0,0,0.51) (root/ui.py:1831-1873) --
   which is exactly the nine-slice tools/import_intro_ui.py already writes as
   thinboard.png, drawn here at ThinBoard's own 16px corner. */
.quest-dialog {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 60;
  box-sizing: border-box;
  width: ${QUEST_BOARD_WIDTH}px;
  min-height: ${QUEST_BOARD_HEIGHT}px;
  max-height: min(450px, calc(100vh - 32px));
  overflow-y: auto;
  /* OnUpdate puts the event set at the board's corner + 20 and gives it
     board.GetWidth() - 40 (uiquest.py:381-384). */
  padding: ${QUEST_BOARD_INSET}px;
  border: ${BOARD_SLICE}px solid transparent;
  border-image: url("${CHROME_ASSETS.thinboard}") ${BOARD_SLICE} repeat;
  background: ${BOARD_FILL} padding-box;
  pointer-events: auto;
  /* event.GetLineCount(...) * 16 is the original's own line pitch
     (uiquest.py:491,499). */
  font: 12px/16px Tahoma, "Segoe UI", sans-serif;
  color: #e8dcc0;
}
.quest-curtain {
  position: fixed;
  inset: 0;
  z-index: 55;
  pointer-events: none;
  visibility: hidden;
}
.quest-curtain-bar {
  position: absolute;
  left: 0;
  width: 100%;
  height: 12vh;
  background: #000;
  transition: transform 250ms ease-out;
}
.quest-curtain-top { top: 0; transform: translateY(-100%); }
.quest-curtain-bottom { bottom: 0; transform: translateY(100%); }
.quest-curtain-fade {
  position: absolute;
  inset: 0;
  background: #000;
  opacity: 0;
  transition: opacity 250ms ease-out;
}
.quest-curtain[data-state="open"],
.quest-curtain[data-state="closing"] { visibility: visible; }
.quest-curtain[data-state="open"] .quest-curtain-top,
.quest-curtain[data-state="open"] .quest-curtain-bottom {
  transform: translateY(0);
}
.quest-curtain[data-state="open"] .quest-curtain-fade { opacity: 0; }
.quest-curtain[data-state="closing"] .quest-curtain-top { transform: translateY(-100%); }
.quest-curtain[data-state="closing"] .quest-curtain-bottom { transform: translateY(100%); }
.quest-curtain[data-state="closing"] .quest-curtain-fade { opacity: 1; }
/* OpenQuestWindow (interfacemodule.py:643-663): for any skin but NOWINDOW
   the dialog is Lock()ed and __HideWindows() (:1129-1160) takes down the
   task bar, character window, inventory, minimap, guild, messenger, chat,
   party and game buttons -- plus the energy bar, expanded task bar and both
   dragon-soul windows when they exist -- along with every quest letter and
   whisper button. __ShowWindows (:1162-1171) puts back exactly the ones
   that were up, which is what hiding by an ancestor attribute does for free:
   the hidden windows keep their own state. */
.quest-modal-lock {
  position: fixed;
  inset: 0;
  z-index: 59;
  background: transparent;
  pointer-events: auto;
}
[data-quest-modal="1"] .legacy-taskbar,
[data-quest-modal="1"] .expand-taskbar,
[data-quest-modal="1"] .charwin,
[data-quest-modal="1"] .bag,
[data-quest-modal="1"] .minimap,
[data-quest-modal="1"] .guild,
[data-quest-modal="1"] .friends,
[data-quest-modal="1"] .chat,
[data-quest-modal="1"] .party,
[data-quest-modal="1"] .energybar,
[data-quest-modal="1"] .dragonsoul-panel,
[data-quest-modal="1"] .dragonsoul-refine-panel,
[data-quest-modal="1"] .quest-letters,
[data-quest-modal="1"] .quest-log {
  /* visibility, not display: the 3D view is sized by the flow these windows
     share, and the original's own windows are overlays whose hiding never
     resizes the world. Taking them out of layout instead moves the camera,
     which is a thing __HideWindows cannot do. */
  visibility: hidden !important;
  pointer-events: none !important;
}
/* The pictures a script puts in the window. Each is ui.ImageBox-shaped: it
   draws at the texture's own size unless the handler resizes it, and it is
   simply not there when the file did not load -- LoadImage raising
   RuntimeError is the case every one of uiquest.py's handlers swallows
   (:713-718, :768-786, :797-805, :820-828, :851-857). onerror is the
   browser's own version of that raise. */
.quest-board-art { image-rendering: pixelated; }
/* OnImage: ui.MakeImageBox(self.board, filename, x, y) (uiquest.py:709-718).
   The board's own padding is the event set's 20px inset, so the x and y the
   script names are measured from the board's frame. */
.quest-board-image {
  position: absolute;
  z-index: 1;
}
/* OnBackgroundImage (uiquest.py:859-904): the board is centred on both axes,
   grown to the picture when the picture is bigger, and HideInternal()d --
   the thinboard frame goes away and the picture is the window. */
.quest-dialog[data-quest-bg="1"] {
  border-image: none;
  border-color: transparent;
  background: transparent;
}
.quest-board-background {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 0;
}
.quest-dialog-title, .quest-dialog-body, .quest-buttons { position: relative; z-index: 2; }
/* OnLeftImage (uiquest.py:807-828): a 400x450 box at the window's own origin,
   with the board pushed to x = 160. */
.quest-board-left {
  position: absolute;
  left: -160px;
  top: 0;
  width: 400px;
  height: 450px;
  object-fit: contain;
  z-index: 1;
}
.quest-dialog[data-quest-left="1"] { margin-left: 160px; }
/* OnTopImage (uiquest.py:830-857): 350 wide, 10 px above the board's top. */
.quest-board-top {
  position: absolute;
  left: 0;
  width: ${QUEST_BOARD_WIDTH}px;
  object-fit: contain;
  z-index: 1;
}
/* OnInsertImage (uiquest.py:747-786): the nth of total pictures sits at
   board.GetWidth() * (index + 1) / (total + 1), centred on that column. */
.quest-board-inserts { position: relative; display: block; margin: 6px 0; }
.quest-board-insert {
  position: absolute;
  transform: translateX(-50%);
  top: 0;
}
.quest-board-insert-title {
  position: absolute;
  transform: translateX(-50%);
  text-align: center;
  white-space: nowrap;
  color: #ffe6ba;
}
/* ToolTipImageBox.CreateToolTip / OnMouseOverIn (uiquest.py:27-49): a tooltip
   under the picture, centred, shown while the pointer is over it. */
.quest-image-tooltip {
  position: fixed;
  z-index: 61;
  transform: translateX(-50%);
  padding: 5px 8px;
  max-width: 240px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.82);
  border: 1px solid rgba(255, 236, 170, 0.3);
  color: #e8dcc0;
  text-align: center;
  pointer-events: none;
}
.quest-image-tooltip-title { color: #ffe6ba; font-weight: 600; }
/* OnTitleImage (uiquest.py:790-805): centred horizontally and put at
   GetScreenHeight() - 75/2 - 32/2 above the screen's foot, alpha 0 and then
   driven by OnUpdate (:384-404) -- 0.5 s in, 1.0 s held, 0.5 s out. */
.quest-title-banner {
  position: fixed;
  left: 50%;
  bottom: 53px;
  transform: translateX(-50%);
  z-index: 62;
  pointer-events: none;
  image-rendering: pixelated;
  opacity: 0;
  animation: quest-title-banner 2s linear 1 forwards;
}
@keyframes quest-title-banner {
  0% { opacity: 0; }
  25% { opacity: 1; }
  75% { opacity: 1; }
  100% { opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .quest-title-banner { animation: none; opacity: 1; }
}
/* EventCurtain (uiquest.py:106-192): a screen-sized bar whose colour is
   white (COLOR_WHITE 0.0) or black (COLOR_BLACK 1.0) and whose alpha walks
   to 1 on an *_OUT and back to 0 on an *_IN. Separate from the letterbox
   above it, which the browser opens and closes on its own. */
.quest-event-curtain {
  position: fixed;
  inset: 0;
  z-index: 63;
  pointer-events: none;
  opacity: 0;
  background: #000;
}
.quest-event-curtain[data-colour="white"] { background: #fff; }
.quest-event-curtain[data-run="out"] { opacity: 1; }
.quest-dialog-title {
  color: #ffe6ba;
  font-weight: 600;
  margin-bottom: 8px;
}
.quest-line { white-space: pre-wrap; margin-bottom: 6px; }
.quest-line-reward { color: #ffc8c8; }
.quest-line-item {
  color: #ffe6ba;
  display: flex;
  align-items: center;
  gap: 8px;
}
.quest-item-icon {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  border: 1px solid rgba(255, 236, 170, 0.45);
  border-radius: 3px;
}
/* The bordered square stays -- it is what says "an item" on a line of speech --
   and the icon fills it when the sheet has one. Height, not width: a 32x96
   icon keeps its shape and the row keeps its height. */
.quest-item-icon.item-icon {
  width: auto;
  height: 20px;
  border-color: rgba(255, 236, 170, 0.25);
}
/* uiquest.py's BarButton (:196-229) is not sprited art: it renders a
   translucent bar under white text, ColorUp 0x40999999 / ColorDown
   0x40aaaacc / ColorOver 0x40ddddff (:197-199, OnRender :213-216), with
   SetTextColor(0xffffffff) (:501). Sizes and the 32px pitch are
   MakeEachButton (:498-499: 200x26, centred, y stepping by i*2*16),
   MakeNextButton (:441-445: 100x26, centred) and
   MakeNextPrevPageButton (:507: 52x26). */
.quest-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 5px;
}
.quest-button {
  flex: 0 0 auto;
  box-sizing: border-box;
  width: ${QUEST_ANSWER_BUTTON_WIDTH}px;
  height: ${QUEST_BUTTON_HEIGHT}px;
  padding: 0 6px;
  border: 0;
  border-radius: 0;
  background: rgba(153, 153, 153, 0.25);
  color: #ffffff;
  font: inherit;
  line-height: ${QUEST_BUTTON_HEIGHT}px;
  cursor: pointer;
}
.quest-button:hover { background: rgba(221, 221, 255, 0.25); }
.quest-button:active { background: rgba(170, 170, 204, 0.25); }
/* The lone Continue/OK/Cancel button is MakeNextButton's 100x26. */
.quest-buttons > .quest-button:only-child { width: ${QUEST_NEXT_BUTTON_WIDTH}px; }
.quest-choice-pages { display: flex; justify-content: center; gap: 6px; }
.quest-choice-page { flex: 0 0 auto; width: ${QUEST_PAGE_BUTTON_WIDTH}px; }
/* QuestDialog(skin == 3), the level-up scroll (uiquest.py:264). The script's
   background is forced to 256x333 (:873-876) and the board takes that size,
   190 px left of centre (:885-887,898-899); the event set starts sy = 100
   below the board's top plus the usual 20 (:372-375,888-889); MakeEachButton
   draws each answer as a 106x26 BarButton in translucent black with black
   text (:487-493), while MakeNextButton keeps its ordinary bar (:431-445). */
.quest-dialog[data-quest-skin="3"] {
  width: 256px;
  min-height: 333px;
  margin-left: -190px;
  padding-top: ${QUEST_BOARD_INSET + 100}px;
}
.quest-dialog[data-quest-skin="3"] .quest-board-background { width: 256px; height: 333px; }
.quest-dialog[data-quest-skin="3"] .quest-buttons[data-quest-answers="1"] .quest-choice {
  width: 106px;
  background: rgba(0, 0, 0, 0.314);
  color: #000000;
}
.quest-dialog[data-quest-skin="3"] .quest-buttons[data-quest-answers="1"] .quest-choice:hover { background: rgba(96, 96, 96, 0.314); }
.quest-dialog[data-quest-skin="3"] .quest-buttons[data-quest-answers="1"] .quest-choice:active { background: rgba(64, 64, 64, 0.314); }
.quest-input {
  width: 100%; box-sizing: border-box; padding: 8px 10px; margin-top: 8px;
  border-radius: 7px; border: 1px solid rgba(255, 236, 170, 0.3);
  background: rgba(0, 0, 0, 0.32); color: #f4eee2; font: inherit;
}
.quest-prompt-message { white-space: pre-wrap; margin-bottom: 8px; }
.quest-item-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  width: 100%;
  gap: 3px;
}
.quest-item-choice { display: flex; align-items: center; gap: 5px; min-width: 0; }
.quest-item-grid .quest-item-choice { min-width: 0; padding: 4px; justify-content: center; }
.quest-item-empty-slot {
  min-height: 32px;
  border: 1px solid rgba(255, 236, 170, 0.16);
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.18);
}
.quest-item-choice img { max-height: 24px; width: auto; }
/* The letters. __ArrangeQuestButton (interfacemodule.py:1327-1354) puts
   them down the LEFT edge -- xPos = 20, or 100 + 30 while the party window
   is up -- starting at yPos = 170 * screenHeight / 600 and stepping 63 px,
   wrapping into a fresh column 100 px to the right after
   (screenHeight - 330) / 63 of them. The column wrap is flex-wrap on a
   height-bounded column, which is the same rule stated once instead of
   arithmetic done per button. */
.quest-letters {
  position: fixed;
  left: ${questLetterX(false)}px;
  top: calc(100vh * 170 / 600);
  max-height: calc(100vh - 330px);
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 0;
  z-index: 36;
  pointer-events: none;
}
.quest-letter {
  position: relative;
  pointer-events: auto;
  width: 100px;
  height: 63px;
  display: flex;
  align-items: flex-start;
  padding: 0;
  border: 0;
  background: none;
  color: #ffe6ba;
  font: 11px/1.25 system-ui, sans-serif;
  text-align: left;
  cursor: pointer;
}
/* SetUpVisual(GetLetterCloseImageName()) / SetOverVisual + SetDownVisual(
   GetLetterOpenImageName()) (interfacemodule.py:1299-1302): the pack's own
   32x32 season1/icon/scroll_close.tga and scroll_open.tga
   (localeinfo.py:624-629), imported by tools/import_game_ui.py. Steady: only
   a non-IsEUROPE() client calls btn.Flash() (interfacemodule.py:1293-1303),
   and this client's service is "EUROPE" (Locale.cpp:21). */
.quest-letter-seal {
  flex: 0 0 auto;
  width: ${QUEST_LETTER_ICON}px;
  height: ${QUEST_LETTER_ICON}px;
  background: url("${CHROME_ASSETS.letterClose}") center / ${QUEST_LETTER_ICON}px ${QUEST_LETTER_ICON}px no-repeat;
  image-rendering: pixelated;
}
.quest-letter:hover .quest-letter-seal,
.quest-letter:focus-visible .quest-letter-seal,
.quest-letter:active .quest-letter-seal {
  background-image: url("${CHROME_ASSETS.letterOpen}");
}
/* SetToolTipText(name, -20, 35) (interfacemodule.py:1314-1315): a TextLine at
   x + GetWidth()/2 (ui.py:992-1003) of a button SetUpVisual sized to its 32px
   letter (PythonWindow.cpp:1634), so 4 px left of the letter and 35 px down,
   its text left-aligned. WhisperButton keeps it shown (uiwhisper.py:17-31) and
   a TextLine is never clipped, so a long name runs into the next column 100 px
   over exactly as the original's does. SetOutline (ui.py:998) rings it in
   0xff000000 (GrpTextInstance.cpp:1129). */
.quest-letter-name {
  position: absolute;
  left: -4px;
  top: 35px;
  white-space: nowrap;
  color: #fff;
  text-shadow: -1px 0 #000, 1px 0 #000, 0 -1px #000, 0 1px #000;
}
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export function continueLabel(final: boolean): string {
  return final ? "OK" : "Continue";
}

/**
 * Paint one page's speech into `body` and hand back the commands its text
 * carried. The letters of a line are its speech and its `[...]` boxes are
 * instructions to the window, which is the split `script::Group::Create` makes
 * before `CPythonEventManager` ever sees a line (`parser.cpp:277-400`): a line
 * that is nothing but a tag -- `[BGIMAGE src;level_bg.tga]`,
 * `[ADDMAPSIGNAL x;..|y;..]`, `[DELAY value;150]` -- draws no row at all.
 */
export function paintQuestLines(
  body: HTMLElement,
  lines: readonly QuestLine_[],
): readonly QuestCommand[] {
  const doc = body.ownerDocument;
  const commands: QuestCommand[] = [];
  for (const line of lines) {
    const script = splitQuestScript(line.text);
    commands.push(...script.commands);
    const hasIcon = line.kind === QUEST_LINE_ITEM;
    if (script.letters === "" && !hasIcon) continue;
    const row = elem(doc, "div", "quest-line");
    if (line.kind === QUEST_LINE_REWARD) row.classList.add("quest-line-reward");
    if (hasIcon) {
      row.classList.add("quest-line-item");
      const icon = elem(doc, "span", "quest-item-icon");
      // The vnum stays on the node whether or not it draws: it is what the
      // icon sheet is keyed by, and a test -- or a devtools inspection of a
      // line that came out blank -- can read which item the server meant.
      icon.dataset.vnum = String(line.itemVnum);
      // An empty bordered square when this build has no painting for it. The
      // server already sent the name beside it, so the line still reads.
      applyItemIcon(icon, line.itemVnum);
      row.append(icon);
    }
    const text = doc.createElement("span");
    text.textContent = script.letters;
    row.append(text);
    body.append(row);
  }
  return commands;
}


/** One `[NAME arg;value|arg;value]` command out of the script stream. */
export interface QuestCommand {
  readonly name: string;
  readonly args: Readonly<Record<string, string>>;
}

/** Everything one page's text says, split the way `script::Group::Create`
 *  splits it (`parser.cpp:277-400`): a `[...]` box is a command and every
 *  other character is a letter. The first space inside the box separates the
 *  name from the arguments, `|` separates argument pairs and `;` separates an
 *  argument's name from its value; names, values and the box itself are
 *  right-trimmed, and a box with no space at all is a bare command name.
 *
 *  Applied to whole lines rather than only to the ones the server marked as a
 *  tag, because that is where the tags really are: `questscroll5.quest:389`
 *  returns `"[WINDOW_SIZE width;360|height;280]"` glued to the front of a
 *  mission description that then travels as ordinary speech.
 */
export function splitQuestScript(
  text: string,
): { readonly commands: readonly QuestCommand[]; readonly letters: string } {
  const commands: QuestCommand[] = [];
  let letters = "";
  let at = 0;
  for (;;) {
    const open = text.indexOf("[", at);
    if (open < 0) {
      letters += text.slice(at);
      return { commands, letters };
    }
    const close = text.indexOf("]", open + 1);
    // `!box_end` is a parse error that abandons the rest of the stream
    // (`parser.cpp:332-336`); an unterminated box takes nothing with it here
    // either, and what came before it is still speech.
    if (close < 0) {
      letters += text.slice(at);
      return { commands, letters };
    }
    letters += text.slice(at, open);
    const box = text.slice(open + 1, close).replace(/\s+$/, "");
    const space = box.indexOf(" ");
    if (space < 0) {
      if (box !== "") commands.push({ name: box, args: {} });
    } else {
      const args: Record<string, string> = {};
      for (const pair of box.slice(space + 1).split("|")) {
        const semicolon = pair.indexOf(";");
        if (semicolon < 0) continue;
        const name = pair.slice(0, semicolon).replace(/^\s+|\s+$/g, "");
        if (name !== "") args[name] = pair.slice(semicolon + 1).replace(/\s+$/, "");
      }
      commands.push({ name: box.slice(0, space), args });
    }
    at = close + 1;
  }
}

/** `d:/ymir work/ui/game/questboard/` as `tools/build_quest_board.py` writes
 *  it. */
const QUEST_BOARD_URL = "/questboard";

/** `__GetQuestImageFileName` (`uiquest.py:588-592`): a name whose second
 *  character is not a colon is relative to `QUEST_BOARD_IMAGE_DIR`; anything
 *  else is already a full path. A full path names a file off this disk and has
 *  no browser URL, which is the `RuntimeError` branch every caller already
 *  has (`uiquest.py:713-718,768-786,797-805,820-828,851-857`).
 */
export function questImageUrl(src: string): string | null {
  if (src === "" || src.length > 1 && src[1] === ":") return null;
  const stem = src.replace(/\.[^./]*$/, "");
  if (stem === "" || /[/\\]/.test(stem)) return null;
  return `${QUEST_BOARD_URL}/${stem}.png`;
}

/** One `[IMAGE x;..|y;..|src;..]` picture, placed inside the board. */
export interface QuestInlineImage {
  readonly x: number;
  readonly y: number;
  readonly src: string;
}

/** One `[INSERT_IMAGE ...]` picture, spaced across the board and carrying its
 *  own hover tooltip (`ToolTipImageBox`, `uiquest.py:20-52`). */
export interface QuestInsertImage {
  readonly src: string;
  readonly underTitle: string;
  readonly title: string;
  /** `desc.replace("|", "/")` then split on `/` (`uiquest.py:34-37`). The `|`
   *  never survives the argument split, so only `/` is left to break on. */
  readonly desc: readonly string[];
  readonly index: number;
  readonly total: number;
}

/** What one page's commands tell the dialogue window to draw. */
export interface QuestBoardArt {
  readonly background: string | null;
  readonly left: string | null;
  readonly top: string | null;
  readonly title: string | null;
  readonly images: readonly QuestInlineImage[];
  readonly inserts: readonly QuestInsertImage[];
  readonly size: { readonly width: number; readonly height: number } | null;
}

export const NO_QUEST_BOARD_ART: QuestBoardArt = {
  background: null, left: null, top: null, title: null,
  images: [], inserts: [], size: null,
};

/** `atoi` on an absent or unparseable argument is zero (`GetArgument` returns
 *  an empty string for a name the list has not got, `PythonEventManager.cpp:
 *  43-46`). */
function tagInt(args: Readonly<Record<string, string>>, name: string): number {
  const parsed = Number.parseInt(args[name] ?? "", 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * The picture commands of one page, in the order `CPythonEventManager` would
 * have run them (`PythonEventManager.cpp:498-545,674-677`). The last of each
 * single-slot kind wins, because each of `OnLeftImage`, `OnTopImage`,
 * `OnBackgroundImage` and `OnTitleImage` overwrites the one box it owns.
 */
export function questBoardArt(commands: readonly QuestCommand[]): QuestBoardArt {
  let background: string | null = null;
  let left: string | null = null;
  let top: string | null = null;
  let title: string | null = null;
  let size: { width: number; height: number } | null = null;
  const images: QuestInlineImage[] = [];
  const inserts: QuestInsertImage[] = [];
  for (const command of commands) {
    const src = command.args.src ?? "";
    switch (command.name) {
      case "BGIMAGE": background = src; break;
      case "LEFTIMAGE": left = src; break;
      case "TOPIMAGE": top = src; break;
      case "TITLE_IMAGE": title = src; break;
      case "IMAGE":
        images.push({ x: tagInt(command.args, "x"), y: tagInt(command.args, "y"), src });
        break;
      case "INSERT_IMAGE": {
        const name = command.args.image_name ?? "";
        // The empty-name branch is the item icon, which the server already
        // sends as a `QuestLine` of its own kind (`PythonEventManager.cpp:
        // 536-541`; `host.py`'s `say_item_vnum`).
        if (name === "") break;
        inserts.push({
          src: name,
          underTitle: command.args.title ?? "",
          title: command.args.title ?? "",
          desc: (command.args.desc ?? "").split("/").filter((line) => line !== ""),
          index: tagInt(command.args, "index"),
          total: Math.max(1, tagInt(command.args, "total")),
        });
        break;
      }
      case "WINDOW_SIZE":
        size = { width: tagInt(command.args, "width"), height: tagInt(command.args, "height") };
        break;
      default: break;
    }
  }
  return { background, left, top, title, images, inserts, size };
}

/** How `EventCurtain` was told to run: which way, and to which colour. */
export interface QuestCurtainRun {
  /** `FADE_OUT`/`WHITE_OUT` go to opaque; `FADE_IN`/`WHITE_IN` come back. */
  readonly direction: "out" | "in";
  /** `EventCurtain.COLOR_BLACK` 1.0 / `COLOR_WHITE` 0.0 (`uiquest.py:110-111`). */
  readonly colour: "black" | "white";
  /** Seconds for a full sweep. `__StartFade` keeps `DEFAULT_FADE_SPEED` 0.035
   *  when the argument is zero, and the alpha moves by `speed` a frame
   *  (`uiquest.py:159-166,171-192`), so a sweep is `1 / (speed * 60)` seconds
   *  at the original's own 60 Hz. */
  readonly seconds: number;
}

/** `EventCurtain.DEFAULT_FADE_SPEED` (`uiquest.py:113`). */
export const QUEST_FADE_SPEED = 0.035;

/** The original's frame rate, which is what turns a per-frame alpha step into
 *  a duration (`CPythonApplication::Process` runs the UI at 60 Hz). */
export const FRAMES_PER_SECOND = 60;

/** The last fade one page's commands asked for, or null when it asked for
 *  none. Last rather than every one, because a page that fades twice has only
 *  one screen to fade. */
export function questCurtainRun(
  commands: readonly QuestCommand[],
): QuestCurtainRun | null {
  let run: QuestCurtainRun | null = null;
  for (const command of commands) {
    const known = QUEST_CURTAIN_TAGS[command.name];
    if (known === undefined) continue;
    const asked = Number.parseFloat(command.args.speed ?? "");
    const speed = Number.isFinite(asked) && asked !== 0 ? Math.abs(asked) : QUEST_FADE_SPEED;
    run = { ...known, seconds: 1 / (speed * FRAMES_PER_SECOND) };
  }
  return run;
}

const QUEST_CURTAIN_TAGS: Readonly<
  Record<string, { direction: "out" | "in"; colour: "black" | "white" }>
> = {
  FADE_OUT: { direction: "out", colour: "black" },
  FADE_IN: { direction: "in", colour: "black" },
  WHITE_OUT: { direction: "out", colour: "white" },
  WHITE_IN: { direction: "in", colour: "white" },
};


export class QuestCurtainPresentation {
  private readonly root: HTMLElement;
  private readonly fade: HTMLElement;
  /** `EventCurtain` itself (`uiquest.py:106-192`), which is a second, whole-
   *  screen bar the script drives and not the letterbox this class opens. */
  private readonly curtain: HTMLElement;

  constructor(container: HTMLElement) {
    const doc = container.ownerDocument;
    installStyle(doc);
    this.curtain = elem(doc, "div", "quest-event-curtain");
    this.curtain.setAttribute("aria-hidden", "true");
    container.append(this.curtain);
    this.root = elem(doc, "div", "quest-curtain");
    this.root.dataset.state = "closed";
    this.root.setAttribute("aria-hidden", "true");

    const top = elem(doc, "div", "quest-curtain-bar quest-curtain-top");
    const bottom = elem(doc, "div", "quest-curtain-bar quest-curtain-bottom");
    this.fade = elem(doc, "div", "quest-curtain-fade");
    this.fade.addEventListener("transitionend", (event) => {
      if (event.propertyName === "opacity" && this.root.dataset.state === "closing") {
        this.root.dataset.state = "closed";
      }
    });
    this.root.append(this.fade, top, bottom);
    container.append(this.root);
  }

  get state(): "closed" | "open" | "closing" {
    const state = this.root.dataset.state;
    return state === "open" || state === "closing" ? state : "closed";
  }

  /** Letterbox and fade in behind the server's first page. */
  open(): void {
    // A forced layout makes reopening during a previous close transition a
    // real transition in Chromium, rather than a class change coalesced away.
    this.root.dataset.state = "closed";
    this.root.getBoundingClientRect();
    this.root.dataset.state = "open";
    this.root.setAttribute("aria-hidden", "false");
    this.fade.setAttribute("aria-hidden", "true");
  }

  /** Fade to black while the dialog is removed, then move the bars outward. */
  close(): void {
    if (this.state === "closed") return;
    this.root.dataset.state = "closing";
    this.root.setAttribute("aria-hidden", "true");
    this.run(null);
  }

  /** What `EventCurtain` is doing: the tag that started it, or "" for none. */
  get eventCurtain(): string {
    return this.curtain.dataset.run === undefined
      ? ""
      : `${this.curtain.dataset.colour ?? "black"}-${this.curtain.dataset.run}`;
  }

  /**
   * Run the fade one page asked for, or clear one that is standing.
   *
   * `FadeOut`/`WhiteOut` start from alpha 0 and `FadeIn`/`WhiteIn` from alpha
   * 1 (`uiquest.py:143-157`), so the sweep's starting opacity is set with the
   * transition off and the destination applied afterwards; without that a
   * `FadeIn` following a `FadeOut` would have nothing to travel from.
   */
  run(asked: QuestCurtainRun | null): void {
    if (asked === null) {
      delete this.curtain.dataset.run;
      delete this.curtain.dataset.colour;
      this.curtain.style.transition = "";
      return;
    }
    this.curtain.style.transition = "none";
    this.curtain.dataset.colour = asked.colour;
    this.curtain.dataset.run = asked.direction === "out" ? "in" : "out";
    // A forced layout so the browser has the starting opacity to move from.
    this.curtain.getBoundingClientRect();
    this.curtain.style.transition = `opacity ${asked.seconds}s linear`;
    this.curtain.dataset.run = asked.direction;
  }
}

export function subtitleFor(entry: QuestEntry_): string {
  if (entry.counterName) return `${entry.counterName} ${entry.counter}`;
  if (entry.clockName) {
    const seconds = Math.max(0, entry.clockSeconds);
    return `${entry.clockName} ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  }
  return entry.targetDesc;
}

export function questModalCapturesKeys(
  dialog: { readonly showing: boolean },
  prompt: { readonly showing: boolean } | null,
): boolean {
  return dialog.showing || (prompt !== null && prompt.showing);
}

/** The bottom-of-screen conversation window. */
export class QuestDialogPanel {
  private readonly root: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly body: HTMLElement;
  private readonly buttons: HTMLElement;
  private readonly presentation: QuestCurtainPresentation;
  private readonly container: HTMLElement;
  /** The modal sheet `Lock()` stands for. */
  private readonly lock: HTMLElement;
  /** The page currently on screen, or null when nothing is open. */
  private page: QuestDialog_ | null = null;
  /** The zero-based group of eight answer buttons currently shown. */
  private choicePage = 0;
  private focusIndex = 0;
  /** `ToolTipImageBox`'s one tooltip, built once and moved (`uiquest.py:27-49`). */
  private readonly imageTooltip: HTMLElement;
  /** `self.imgTitle`: a `"TOP_MOST"` box over the whole screen, not a child of
   *  the board (`uiquest.py:790-805`). */
  private titleBanner: HTMLImageElement | null = null;

  constructor(
    container: HTMLElement,
    private readonly actions: QuestActions = {},
  ) {
    installStyle(container.ownerDocument);
    this.presentation = new QuestCurtainPresentation(container);
    // `__HideWindows` names windows the interface owns, not the ones that
    // happen to share this panel's parent -- the minimap and the task bar are
    // mounted elsewhere. The flag therefore goes on the document element, and
    // the selectors below reach every window from there.
    this.container = container.ownerDocument.documentElement;
    // `q.Lock()` (`interfacemodule.py:651`) is `wndMgr.Lock`: the dialog takes
    // every pick until it closes. A transparent full-screen sheet under the
    // board is the browser's same rule -- nothing behind it is clickable, and
    // the board itself sits above it.
    this.lock = elem(container.ownerDocument, "div", "quest-modal-lock");
    this.lock.hidden = true;
    this.lock.setAttribute("aria-hidden", "true");
    container.append(this.lock);
    this.root = elem(container.ownerDocument, "div", "quest-dialog");
    this.root.hidden = true;

    this.titleEl = elem(container.ownerDocument, "div", "quest-dialog-title");

    this.body = elem(container.ownerDocument, "div", "quest-dialog-body");

    this.buttons = elem(container.ownerDocument, "div", "quest-buttons");

    this.imageTooltip = elem(container.ownerDocument, "div", "quest-image-tooltip");
    this.imageTooltip.hidden = true;
    this.imageTooltip.setAttribute("aria-hidden", "true");
    container.append(this.imageTooltip);

    this.root.append(this.titleEl, this.body, this.buttons);
    this.root.addEventListener("keydown", (event) => this.onKeyDown(event));
    // The legacy quest window closes on Escape and returns True
    // (`uiquest.py:641-662`). Handle it at the document level because the
    // dialog does not steal focus from the world; an Escape pressed with the
    // body focused must still dismiss the visible conversation.
    container.ownerDocument.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.showing || this.page === null) return;
      event.preventDefault();
      event.stopPropagation();
      // Escape presses the last button (`uiquest.py:658`): a menu's Close, a
      // script select's own last option. A page with no choices has none.
      const choices = this.page.choices.length;
      this.answer(choices > 0 ? choices : 1);
    });
    container.append(this.root);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  /** True while `Lock()` + `__HideWindows()` are in force. */
  get locked(): boolean {
    return this.container.dataset.questModal === "1" && !this.lock.hidden;
  }

  /** The presentation state is observable without exposing quest authority. */
  get curtainState(): "closed" | "open" | "closing" {
    return this.presentation.state;
  }

  /** What the panel would send if the nth button were pressed. For tests. */
  get openToken(): number {
    return this.page === null ? QUEST_NO_DIALOG : this.page.token;
  }

  update(page: QuestDialog_): void {
    if (page.token === QUEST_NO_DIALOG) {
      this.close();
      return;
    }

    const first = !this.showing;
    if (first) this.presentation.open();
    this.page = page;
    this.root.dataset.questSkin = String(page.skin ?? QUEST_SKIN_NORMAL);
    this.titleEl.textContent = page.title;
    this.titleEl.hidden = page.title.length === 0;

    this.body.replaceChildren();
    const commands = paintQuestLines(this.body, page.lines);
    this.drawArt(questBoardArt(commands));
    this.presentation.run(questCurtainRun(commands));

    this.choicePage = 0;
    this.renderChoices();

    this.root.hidden = false;
    this.lock.hidden = false;
    this.container.dataset.questModal = "1";
    if (first) this.actions.onOpened?.();
  }

  /** Take the window down without answering. Used when the player dies or leaves. */
  close(): void {
    if (this.showing) this.presentation.close();
    this.root.hidden = true;
    this.lock.hidden = true;
    delete this.container.dataset.questModal;
    this.page = null;
    delete this.root.dataset.questSkin;
    this.drawArt(NO_QUEST_BOARD_ART);
    this.root.style.marginTop = "";
    this.body.replaceChildren();
    this.buttons.replaceChildren();
  }

  /**
   * Draw the page's pictures and resize the board the way the script asked.
   *
   * Every box is rebuilt from the page rather than kept, because the original
   * clears `self.images` between pages (`uiquest.py:339-345`) and the four
   * single-slot boxes are overwritten by whichever command named them last.
   */
  private drawArt(art: QuestBoardArt): void {
    for (const stale of [...this.root.querySelectorAll(".quest-board-art")]) stale.remove();
    this.titleBanner?.remove();
    this.titleBanner = null;
    this.imageTooltip.hidden = true;

    // `OnSize` (`uiquest.py:787-788`) is the whole of `[WINDOW_SIZE]`: the
    // board takes the stated size and nothing else moves.
    this.root.style.width = art.size === null ? "" : `${art.size.width}px`;
    this.root.style.minHeight = art.size === null ? "" : `${art.size.height}px`;

    const background = art.background === null ? null : this.picture(art.background);
    if (background !== null) {
      background.classList.add("quest-board-background");
      // `c.HideInternal()` (`uiquest.py:892`): with a background the board is
      // the picture, so its own frame is not drawn.
      this.root.dataset.questBg = "1";
      this.root.prepend(background);
    } else {
      delete this.root.dataset.questBg;
    }

    const left = art.left === null ? null : this.picture(art.left);
    if (left !== null) {
      left.classList.add("quest-board-left");
      this.root.prepend(left);
    }
    this.root.dataset.questLeft = left === null ? "0" : "1";

    const top = art.top === null ? null : this.picture(art.top);
    if (top !== null) {
      top.classList.add("quest-board-top");
      // `bd.SetPosition(bx, 190)` with the picture at `(bx, 10)` and 170 high
      // is the small-picture case (`uiquest.py:844-849`); a taller one pushes
      // the board to `20 + h` and keeps its own height. Both are "the picture
      // sits above the board", which here is a margin the picture creates.
      top.addEventListener("load", () => {
        const height = Math.max(170, top.naturalHeight);
        top.style.top = `${-height - 10}px`;
        this.root.style.marginTop = `${height + 10}px`;
      });
      this.root.prepend(top);
    } else {
      this.root.style.marginTop = "";
    }

    for (const image of art.images) {
      const node = this.picture(image.src);
      if (node === null) continue;
      node.classList.add("quest-board-image");
      node.style.left = `${image.x}px`;
      node.style.top = `${image.y}px`;
      this.root.append(node);
    }

    if (art.inserts.length > 0) this.body.append(this.insertRow(art.inserts));

    if (art.title !== null) {
      const banner = this.picture(art.title);
      if (banner !== null) {
        banner.classList.add("quest-title-banner");
        this.titleBanner = banner;
        this.container.ownerDocument.body.append(banner);
      }
    }
  }

  /** One `ui.ImageBox`: the picture, or nothing when the file does not load. */
  private picture(src: string): HTMLImageElement | null {
    const url = questImageUrl(src);
    if (url === null) return null;
    const image = this.root.ownerDocument.createElement("img");
    image.className = "quest-board-art";
    image.alt = "";
    image.dataset.src = src;
    image.src = url;
    // `except RuntimeError: pass` / `img.Hide()` -- a picture the client has
    // not got leaves the window as it would have been without the command.
    image.addEventListener("error", () => image.remove());
    return image;
  }

  /** `OnInsertImage`'s row: `total` columns, the nth centred on its own. */
  private insertRow(inserts: readonly QuestInsertImage[]): HTMLElement {
    const doc = this.root.ownerDocument;
    const row = elem(doc, "div", "quest-board-inserts quest-board-art");
    let tallest = 0;
    for (const insert of inserts) {
      const image = this.picture(insert.src);
      if (image === null) continue;
      image.classList.add("quest-board-insert");
      const column = `${((insert.index + 1) * 100) / (insert.total + 1)}%`;
      image.style.left = column;
      image.addEventListener("load", () => {
        tallest = Math.max(tallest, image.naturalHeight);
        row.style.height = `${tallest + (insert.underTitle === "" ? 0 : 19)}px`;
      });
      if (insert.title !== "" && insert.desc.length > 0) {
        this.attachImageTooltip(image, insert.title, insert.desc);
      }
      row.append(image);
      if (insert.underTitle !== "") {
        // `event.InsertTextInline(..., board.GetWidth() * (index+1)/(total+1))`
        // three pixels under the picture (`uiquest.py:775-778`).
        const under = elem(doc, "div", "quest-board-insert-title", insert.underTitle);
        under.style.left = column;
        under.style.top = "calc(100% - 16px)";
        row.append(under);
      }
    }
    return row;
  }

  /** `ToolTipImageBox.CreateToolTip` and its two hover handlers. */
  private attachImageTooltip(
    image: HTMLElement,
    title: string,
    desc: readonly string[],
  ): void {
    const doc = this.root.ownerDocument;
    image.addEventListener("pointerenter", () => {
      this.imageTooltip.replaceChildren(
        elem(doc, "div", "quest-image-tooltip-title", title),
        ...desc.map((line) => elem(doc, "div", "quest-image-tooltip-line", line)),
      );
      const box = image.getBoundingClientRect();
      // `SetPosition(x, yBoard + y + img.GetHeight())` with
      // `SetWindowHorizontalAlignCenter` (`uiquest.py:29-32,767`): under the
      // picture, centred on it.
      this.imageTooltip.style.left = `${box.left + box.width / 2}px`;
      this.imageTooltip.style.top = `${box.bottom}px`;
      this.imageTooltip.hidden = false;
    });
    image.addEventListener("pointerleave", () => {
      this.imageTooltip.hidden = true;
    });
  }

  private makeButton(label: string, choice: number): HTMLButtonElement {
    const button = this.root.ownerDocument.createElement("button");
    button.type = "button";
    button.className = "quest-button quest-choice";
    button.textContent = label;
    button.addEventListener("click", () => this.answer(choice));
    return button;
  }

  private renderChoices(): void {
    const page = this.page;
    if (page === null) return;

    this.buttons.replaceChildren();
    this.focusIndex = 0;
    this.buttons.dataset.questAnswers = page.choices.length === 0 ? "0" : "1";
    if (page.choices.length === 0) {
      this.buttons.append(this.makeButton(continueLabel(page.final), 0));
      return;
    }

    const firstChoice = this.choicePage * QUEST_CHOICES_PER_PAGE;
    const visibleChoices = page.choices.slice(firstChoice, firstChoice + QUEST_CHOICES_PER_PAGE);
    for (const [index, label] of visibleChoices.entries()) {
      this.buttons.append(this.makeButton(label, firstChoice + index + 1));
    }

    if (page.choices.length <= QUEST_CHOICES_PER_PAGE) return;
    const navigation = elem(this.root.ownerDocument, "div", "quest-choice-pages");
    // `MakeQuestion`'s own pagination words (`uiquest.py:475,480`):
    // `localeInfo.UI_PREVPAGE` ("Back") and `UI_NEXTPAGE` ("Next"), not a
    // house "Previous/Next page".
    const previous = this.makePageButton("Back", "quest-choice-previous", -1);
    const next = this.makePageButton("Next", "quest-choice-next", 1);
    previous.hidden = this.choicePage === 0;
    next.hidden = firstChoice + QUEST_CHOICES_PER_PAGE >= page.choices.length;
    navigation.append(previous, next);
    this.buttons.append(navigation);
  }

  private makePageButton(label: string, className: string, step: -1 | 1): HTMLButtonElement {
    const button = this.root.ownerDocument.createElement("button");
    button.type = "button";
    button.className = `quest-button quest-choice-page ${className}`;
    button.textContent = label;
    button.addEventListener("click", () => {
      this.choicePage += step;
      this.renderChoices();
    });
    return button;
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (this.page === null) return;
    const buttons = [...this.buttons.querySelectorAll<HTMLButtonElement>(".quest-choice")];
    if (buttons.length === 0) return;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      const active = buttons.indexOf(this.root.ownerDocument.activeElement as HTMLButtonElement);
      if (active >= 0) this.focusIndex = active;
      this.focusIndex = event.key === "ArrowDown"
        ? (this.focusIndex + 1) % buttons.length
        : (this.focusIndex - 1 + buttons.length) % buttons.length;
      buttons[this.focusIndex]?.focus();
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.key !== "Enter") return;
    const active = buttons.indexOf(this.root.ownerDocument.activeElement as HTMLButtonElement);
    const index = active >= 0 ? active : this.focusIndex;
    event.preventDefault();
    event.stopPropagation();
    this.answer(this.page.choices.length === 0 ? 0 : this.choicePage * QUEST_CHOICES_PER_PAGE + index + 1);
  }

  /** Send an answer. Public so a keyboard shortcut can reach it. */
  answer(choice: number): void {
    const page = this.page;
    if (page === null) return;
    // The window comes down here rather than on the reply, so a page that ends
    // the conversation does not sit on screen waiting for a frame that says
    // nothing. Anything further to say arrives as another page.
    this.close();
    this.actions.onReply?.(page.token, choice);
  }
}

export interface QuestPromptActions {
  onInput?: (token: number, text: string) => void;
  onConfirm?: (token: number, accept: boolean) => void;
  onItem?: (token: number, cell: number) => void;
  onOpened?: () => void;
}

/** A Unicode-safe prefix within `packet.h:770-774`'s 64-byte text payload. */
export function utf8Prefix(text: string, maxBytes: number): string {
  const encoder = new TextEncoder();
  let out = "";
  for (const point of text) {
    if (encoder.encode(out + point).length > maxBytes) break;
    out += point;
  }
  return out;
}

/** `input()`, `confirm()` and `select_item()` in one mutually-exclusive modal. */
export class QuestPromptPanel {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly body: HTMLElement;
  private readonly controls: HTMLElement;
  private readonly itemTooltip: ItemTooltip;
  private token = QUEST_NO_DIALOG;
  private kind: "input" | "confirm" | "item" | null = null;
  private bag: Inventory = { gold: 0, capacity: 0, slots: [], worn: [] };
  private itemPrompt: QuestItemPrompt_ | null = null;
  private confirmTicker: ReturnType<typeof setInterval> | null = null;

  constructor(container: HTMLElement, private readonly actions: QuestPromptActions = {}) {
    installStyle(container.ownerDocument);
    const doc = container.ownerDocument;
    this.root = elem(doc, "div", "quest-dialog quest-prompt");
    this.root.hidden = true;
    this.title = elem(doc, "div", "quest-dialog-title");
    this.body = doc.createElement("div");
    this.controls = elem(doc, "div", "quest-buttons");
    this.itemTooltip = new ItemTooltip(doc);
    this.root.append(this.title, this.body, this.controls);
    container.append(this.root);
    // `QuestionDialog.OnPressEscapeKey` and its input variant both close and
    // consume Escape (`uicommon.py:60-62,271-273`). The browser prompt may not
    // own focus after a redraw, so listen at the document; item selection is
    // intentionally excluded because its legacy window has no Escape hook.
    doc.addEventListener("keydown", (event) => {
      if (
        event.key !== "Escape" ||
        !this.root.isConnected ||
        !this.showing ||
        this.kind === "item"
      ) return;
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });
  }

  get showing(): boolean { return !this.root.hidden; }

  setBag(bag: Inventory): void {
    this.bag = bag;
    if (this.kind === "item" && this.itemPrompt !== null) this.drawItems(this.itemPrompt);
  }

  updateInput(prompt: QuestInputPrompt_): void {
    if (prompt.token === QUEST_NO_DIALOG) return this.close();
    this.open("input", prompt.token);
    this.title.textContent = prompt.title;
    this.title.hidden = prompt.title.length === 0;
    paintQuestLines(this.body, prompt.lines);
    const doc = this.root.ownerDocument;
    const form = elem(doc, "form", "quest-input-form");
    const input = elem(doc, "input", "quest-input");
    input.type = "text";
    input.autocomplete = "off";
    input.addEventListener("input", () => {
      const limited = utf8Prefix(input.value, prompt.maxBytes);
      if (limited !== input.value) input.value = limited;
    });
    const submit = this.button("Submit", () => undefined);
    submit.type = "submit";
    form.append(input, submit);
    const answer = (): void => {
      if (this.kind !== "input" || this.token !== prompt.token) return;
      const typed = utf8Prefix(input.value, prompt.maxBytes);
      this.close();
      this.actions.onInput?.(prompt.token, typed);
    };
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      answer();
    });
    // `QuestDialog.OnIMEReturn` answers the input page itself and returns True
    // (`uiquest.py:664-667`, sending in `Destroy` at `:349-360`), so the
    // manager stops at this window (`PythonWindowManager.cpp:1077-1083`).
    // Escape is left to bubble: the panel's own document listener closes on it.
    input.addEventListener("keydown", (event) => {
      if (yieldToCandidateWindow(event) || event.key !== "Enter") return;
      event.preventDefault();
      event.stopPropagation();
      answer();
    });
    this.controls.append(form);
    input.focus();
  }

  updateConfirm(prompt: QuestConfirmPrompt_): void {
    // A waiting face deliberately has no answer capability; zero with
    // `waiting=false` remains the server's close frame.
    if (prompt.token === QUEST_NO_DIALOG && !prompt.waiting) return this.close();
    this.open("confirm", prompt.token);
    this.title.textContent = prompt.waiting ? "Confirmation" : "Question";
    this.title.hidden = false;
    const message = elem(this.root.ownerDocument, "div", "quest-prompt-message");
    const deadline = Date.now() + Math.max(0, prompt.remainingMs);
    // `QuestionDialogWithTimeLimit.Open` records the deadline and its
    // `OnUpdate` rewrites the remaining-time line every frame
    // (`uicommon.py:300-331`). The duration remains server-owned; this only
    // keeps the already-received advisory time visibly moving between frames.
    const drawRemaining = () => {
      const seconds = Math.ceil(Math.max(0, deadline - Date.now()) / 1000);
      message.textContent = `${prompt.text}${seconds > 0 ? ` (${seconds}s)` : ""}`;
    };
    drawRemaining();
    this.body.append(message);
    if (prompt.remainingMs > 0) this.confirmTicker = setInterval(drawRemaining, 250);
    if (prompt.waiting) return;
    this.controls.append(
      this.button("Yes", () => this.answerConfirm(prompt.token, true)),
      this.button("No", () => this.answerConfirm(prompt.token, false)),
    );
  }

  updateItem(prompt: QuestItemPrompt_): void {
    if (prompt.token === QUEST_NO_DIALOG) return this.close();
    this.itemPrompt = prompt;
    this.open("item", prompt.token);
    this.drawItems(prompt);
  }

  close(): void {
    this.clearConfirmTicker();
    this.itemTooltip.hide();
    this.root.hidden = true;
    this.token = QUEST_NO_DIALOG;
    this.kind = null;
    this.itemPrompt = null;
    this.title.textContent = "";
    this.body.replaceChildren();
    this.controls.replaceChildren();
  }

  private open(kind: "input" | "confirm" | "item", token: number): void {
    this.clearConfirmTicker();
    const first = !this.showing;
    this.kind = kind;
    this.token = token;
    this.itemPrompt = kind === "item" ? this.itemPrompt : null;
    this.body.replaceChildren();
    this.controls.replaceChildren();
    this.root.hidden = false;
    if (first) this.actions.onOpened?.();
  }

  private clearConfirmTicker(): void {
    if (this.confirmTicker === null) return;
    clearInterval(this.confirmTicker);
    this.confirmTicker = null;
  }

  private drawItems(prompt: QuestItemPrompt_): void {
    this.title.textContent = "Choose an item";
    this.title.hidden = false;
    this.body.replaceChildren();
    this.controls.replaceChildren();
    const byCell = new Map(this.bag.slots.map((slot) => [slot.slot, slot]));
    const grid = elem(this.root.ownerDocument, "div", "quest-item-grid");
    let itemCount = 0;
    for (const cell of prompt.cells) {
      const slot = byCell.get(cell);
      if (slot === undefined) continue;
      const button = this.button(
        slot.count > 1 ? `${slot.name} ×${slot.count}` : slot.name,
        () => this.answerItem(prompt.token, cell),
      );
      button.classList.add("quest-item-choice");
      const icon = this.root.ownerDocument.createElement("span");
      applyItemIcon(icon, slot.vnum);
      button.prepend(icon);
      // `SelectItemWindow` wires hover to `SetInventoryItem` and hides it on
      // leave (`uiselectitem.py:38-42,126-133`). Use the shared browser tooltip
      // so this visible choice has the same inspect-before-click affordance as
      // the inventory, without changing the server-owned answer token/cell.
      attachHoverTooltip(button, this.itemTooltip, () => tooltipDataFor(slot));
      grid.append(button);
      itemCount += 1;
    }
    // The original arranges a five-column, 32px slot table, and keeps at least
    // three rows even when it found fewer than fifteen eligible items
    // (`uiselectitem.py:63-72,104-124`). The cells remain server-approved;
    // these inert blanks only preserve the chooser's visible slot geometry.
    const slotCount = Math.max(15, Math.ceil(itemCount / 5) * 5);
    for (let index = itemCount; index < slotCount; index += 1) {
      const empty = elem(this.root.ownerDocument, "div", "quest-item-empty-slot");
      empty.setAttribute("aria-hidden", "true");
      grid.append(empty);
    }
    this.controls.append(grid);
    if (itemCount === 0) {
      const empty = elem(
        this.root.ownerDocument,
        "div",
        "quest-prompt-message",
        "No eligible items in your bag.",
      );
      this.body.append(empty);
    }
    this.controls.append(this.button("Cancel", () => this.answerItem(prompt.token, 0)));
  }

  private button(label: string, action: () => void): HTMLButtonElement {
    const button = this.root.ownerDocument.createElement("button");
    button.type = "button";
    button.className = "quest-button";
    button.textContent = label;
    button.addEventListener("click", action);
    return button;
  }

  private answerConfirm(token: number, accept: boolean): void {
    if (this.kind !== "confirm" || this.token !== token) return;
    this.close();
    this.actions.onConfirm?.(token, accept);
  }

  private answerItem(token: number, cell: number): void {
    if (this.kind !== "item" || this.token !== token) return;
    this.close();
    this.actions.onItem?.(token, cell);
  }
}

/** The corner list of open quests. */
export class QuestLogPanel {
  private readonly root: HTMLElement;
  private readonly list: HTMLElement;
  private fingerprint = "";
  private entries: readonly QuestEntry_[] = [];

  constructor(
    container: HTMLElement,
    private readonly actions: QuestActions = {},
  ) {
    installStyle(container.ownerDocument);
    this.root = elem(container.ownerDocument, "div", "quest-log");
    this.root.hidden = true;

    const heading = elem(container.ownerDocument, "div", "quest-log-title");
    // The shipped hotkey help names this window "Task Window"
    // (`HELP_OPEN_QUEST`, `locale_interface.txt`), and `GAME_QUEST` is
    // "Tasks" -- never "Quests", which the client uses only for the standalone
    // `QUEST_APPEND` notice, not this window's own title.
    heading.textContent = "Tasks";

    this.list = container.ownerDocument.createElement("div");
    this.root.append(heading, this.list);
    container.append(this.root);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get element(): HTMLElement {
    return this.root;
  }

  /** How many rows are drawn. For tests and for the HUD. */
  get count(): number {
    return this.entries.length;
  }

  update(entries: readonly QuestEntry_[]): void {
    const mark = entries
      .map((e) => `${e.index}|${e.title}|${e.counterName}|${e.counter}|${e.targetDesc}`)
      .join("\n");
    if (mark === this.fingerprint) return;
    this.fingerprint = mark;
    this.entries = entries;

    const doc = this.root.ownerDocument;
    this.list.replaceChildren();
    for (const entry of entries) {
      const row = doc.createElement("button");
      row.type = "button";
      row.className = "quest-row";
      row.dataset.index = String(entry.index);

      const title = elem(doc, "div", "quest-row-title");
      // A quest whose current step only placed an arrow has no button title of
      // its own; the arrow's description is what it is about.
      title.textContent = entry.title || entry.targetDesc;
      row.append(title);

      const subtitle = subtitleFor(entry);
      if (subtitle && subtitle !== title.textContent) {
        const line = elem(
          doc,
          "div",
          entry.counterName ? "quest-row-count" : "quest-row-line",
          subtitle,
        );
        row.append(line);
      }

      row.addEventListener("click", () => this.actions.onOpen?.(entry.index));

      // The row's other body. Drawn only when somebody is listening, so the
      // character sheet does not grow a control that sends nothing.
      if (this.actions.onDescribe === undefined) {
        this.list.append(row);
        continue;
      }
      const group = elem(doc, "div", "quest-row-entry");
      const describe = doc.createElement("button");
      describe.type = "button";
      describe.className = "quest-row-info";
      describe.dataset.index = String(entry.index);
      // No shipped locale string names this: the original has no such control,
      // because it reaches `OnInfo` from a whole separate window. A mark and a
      // title rather than an invented caption.
      describe.textContent = "?";
      describe.title = "Description";
      describe.setAttribute("aria-label", `Description: ${title.textContent}`);
      describe.addEventListener("click", (event) => {
        // A sibling, not a child: a button inside a button is not valid HTML
        // and the outer one would swallow the click. Stop it anyway, so a
        // future wrapper cannot turn "read this" into "start this".
        event.stopPropagation();
        this.actions.onDescribe?.(entry.index);
      });
      group.append(row, describe);
      this.list.append(group);
    }
    // Corner card hides when empty; the CharacterWindow QUEST page stays up.
    if (this.root.closest(".charwin-page") === null) {
      this.root.hidden = entries.length === 0;
    } else {
      this.root.hidden = false;
    }
  }
}

/** `__ArrangeQuestButton`'s column x: 100 + 30 while the party window is
 *  shown, 20 otherwise (`interfacemodule.py:1334-1338`). */
export function questLetterX(partyShown: boolean): number {
  return partyShown ? 100 + 30 : 20; // interfacemodule.py:1335,1337
}

export class QuestLetterList {
  private readonly root: HTMLElement;
  private fingerprint = "";
  /** Clicked letters by quest index, each with the name it carried. */
  private readonly clicked = new Map<number, string>();
  /** The drawn letters, newest first, as `questButtonList` holds them. */
  private order: { readonly index: number; readonly name: string }[] = [];

  constructor(
    container: HTMLElement,
    private readonly actions: QuestActions = {},
  ) {
    installStyle(container.ownerDocument);
    this.root = elem(container.ownerDocument, "div", "quest-letters");
    this.root.hidden = true;
    container.append(this.root);
  }

  get element(): HTMLElement {
    return this.root;
  }

  /** Re-run `__ArrangeQuestButton` for the party window's visibility, as
   *  `AddPartyMember`, `RemovePartyMember` and `ExitParty` do
   *  (`interfacemodule.py:682-709`). */
  arrange(partyShown: boolean): void {
    this.root.style.left = `${questLetterX(partyShown)}px`;
  }

  /** How many letters are drawn. */
  get count(): number {
    return this.root.childElementCount;
  }

  update(entries: readonly QuestEntry_[]): void {
    const mark = entries.map((e) => `${e.index}|${e.title}|${e.targetDesc}`).join("\n");
    if (mark === this.fingerprint) return;
    this.fingerprint = mark;

    for (const [index, name] of this.clicked) {
      if (!entries.some((e) => e.index === index && e.title === name)) this.clicked.delete(index);
    }
    // A letter is the QUESTBUTTON event alone (PythonEventManager.cpp:574-597),
    // written only by makequestbutton/send_letter (questlib.lua:64,89), whose
    // name is the title; target.vid only makes an arrow (questlua_target.cpp:47-67).
    const shown = entries.filter((e) => e.title !== "" && this.clicked.get(e.index) !== e.title);
    const kept = this.order.filter((d) => shown.some((e) => e.index === d.index && e.title === d.name));
    for (const entry of shown) {
      // insert(0, btn) for every new or resent letter (interfacemodule.py:1279-1281,1323).
      if (!kept.some((d) => d.index === entry.index)) kept.unshift({ index: entry.index, name: entry.title });
    }
    this.order = kept;
    const doc = this.root.ownerDocument;
    this.root.replaceChildren();
    for (const entry of kept) {
      const name = entry.name;
      const button = doc.createElement("button");
      button.type = "button";
      button.className = "quest-letter";
      button.dataset.index = String(entry.index);
      button.title = name;
      button.setAttribute("aria-label", name);
      button.append(
        elem(doc, "span", "quest-letter-seal"),
        elem(doc, "span", "quest-letter-name", name),
      );
      button.addEventListener("click", () => {
        // `__StartQuest` clicks and destroys the button (interfacemodule.py:1356-1358).
        this.actions.onOpen?.(entry.index);
        this.clicked.set(entry.index, name);
        button.remove();
        this.root.hidden = this.root.childElementCount === 0;
      });
      this.root.append(button);
    }
    this.root.hidden = this.root.childElementCount === 0;
  }
}

